import { useStock } from './StockContext';
import { useClient } from './ClientContext';
import { useSelectedClientScopeWithFallback } from './useSelectedClientScope';
import { useAlert } from '@/hooks/index';
import { useDbFunctions, useEvents } from '@/talons';
import { useTables } from './TablesContext';
import { useDiscountFlex } from './DiscountFlexContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ClientProps, StockProps } from '@/types';
import {
  buildCartDetailsKey,
  getCartBrandKeyFromStorage,
  getOnStorage,
  removeFromStorage,
  setOnStorage,
} from '@/shared';
import stockMock from '@/mocks/stockMock';
import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// milisegundos * segundos * minutos * horas * dias
const MAX_TIME_TO_KEEP_ORDER_NUMBER = 1000 * 60 * 60 * 24 * 8;
export interface CartItems {
  sku: number;
  uid: string;
  quantity: number;
  option_uid: string;
  category_id: number;
  addedAt?: string;
}

export interface CartDetailsProps {
  id?: string;
  total?: number;
  quantity: number;
  subtotal?: number;
  items: CartItems[];
  rcaCod?: number;
}

export interface CartProps {
  cartId: string;
  cart: CartItems[];
  filterTerm: string;
  isSearching: boolean;
  // estoque: StockProps[];
  totalQuantity: number;
  totalVolume: number;
  cartDetails: Record<string, CartDetailsProps>;
  currentCartDetails: CartDetailsProps;
  isAdding: boolean;
  hasOrderNumberSaved: boolean;
  updateLastOrderSendTime: () => void;

  checkLimit: (uid: string, sku: number, quantity: number, updatedCart: CartItems[], showAlert?: boolean) => number;
  addProductToCart: (data: { sku: number; quantity: number; option_uid: string; uid: string; category_id: number }) => void;
  updateProductAmount: (data: { uid: string; sku: number; quantity: number }) => void;
  clearCart: () => void;
  handleRemoveItems: (scopeCartKey?: string) => Promise<void>;
  getLimit: (sku: number) => number;
  getStockMsg: (sku: number) => string | undefined;
  setFilterTerm: Dispatch<SetStateAction<string>>;
  removeProductFromCart: ({ uid }: { uid: string }) => void;
  removeMultipleProductsFromCart: (uids: string[]) => void;
  createEmptyCart: ({ client }: { client: ClientProps }) => void;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  loadedEstoque: RefObject<boolean>;
  setCartLoading: Dispatch<SetStateAction<boolean>>;
  cartLoading: boolean;
  setCartSort: Dispatch<SetStateAction<'inclusao' | 'alfabetica' | 'inclusao-antigos'>>;
  cartSort: 'inclusao' | 'alfabetica' | 'inclusao-antigos';
}

export const CartContext = createContext({} as CartProps);

const CartProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { stockLeft, activeStoreCode } = useStock();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { selectedClient } = useClient();
  const selectedClientScope = useSelectedClientScopeWithFallback();
  const { showAlert } = useAlert();
  const { clearClientDiscount } = useDiscountFlex();

  const loadedEstoque = useRef(false);

  const [selectedBrandKey, setSelectedBrandKey] = useState(() => getCartBrandKeyFromStorage());
  const [isAdding, setIsAdding] = useState(false);
  const [filterTerm, setFilterTerm] = useState('');
  const [cartSort, setCartSort] = useState<'inclusao' | 'alfabetica' | 'inclusao-antigos'>(() => {
    const saved = getOnStorage('cart_sort');
    return saved === 'inclusao' || saved === 'alfabetica' || saved === 'inclusao-antigos' ? saved : 'alfabetica';
  });
  const [cartLoading, setCartLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const currentCartDetailsKey = useMemo(
    () => buildCartDetailsKey(selectedClient?.COD_CLIENTE, selectedBrandKey),
    [selectedClient?.COD_CLIENTE, selectedBrandKey]
  );

  const [cart, setCart] = useState<CartItems[]>(() => {
    const savedCart = getOnStorage('cart_details');
    const key = buildCartDetailsKey(selectedClient?.COD_CLIENTE, getCartBrandKeyFromStorage());
    return savedCart?.[key]?.items ?? [];
  });

  const [cartDetails, setCartDetails] = useState<Record<string, CartDetailsProps>>(() => {
    return getOnStorage('cart_details') || {};
  });

  const [cartId, setCartId] = useState(() => getOnStorage('cart_id') || '');
  const [hasOrderNumberSaved, setHasOrderNumberSaved] = useState(false);

  const currentCartDetails = useMemo(() => {
    if (!currentCartDetailsKey) return { items: [], quantity: 0 };
    return cartDetails?.[currentCartDetailsKey] || { items: [], quantity: 0 };
  }, [cartDetails, currentCartDetailsKey]);

  const cartDetailsWithCurrentClientAlias = useMemo(() => {
    if (!selectedClient?.COD_CLIENTE) return cartDetails;
    return {
      ...cartDetails,
      [selectedClient.COD_CLIENTE]: currentCartDetails,
    };
  }, [cartDetails, currentCartDetails, selectedClient?.COD_CLIENTE]);

  const updateLastOrderSendTime = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return;

    const storedOrders = { ...(getOnStorage('order_numbers') || {}) };
    const scopedKey = buildCartDetailsKey(selectedClient.COD_CLIENTE, selectedBrandKey);
    const legacyKey = String(selectedClient.COD_CLIENTE);
    const lastUpdate = new Date().getTime();
    const existing = storedOrders[scopedKey] ?? storedOrders[legacyKey];

    setOnStorage('order_numbers', {
      ...storedOrders,
      [scopedKey]: {
        orderNumber: existing?.orderNumber ?? '',
        lastUpdate,
      },
    });
  }, [selectedClient?.COD_CLIENTE, selectedBrandKey]);

  const dictEstoque = useMemo(() => {
    // Usa `activeStoreCode` (loja ativa do `selectedBrandPair` validada contra `selectedClient.lojas`)
    // em vez de `selectedClient.COD_LOJA`. Caso contrário, ao trocar de brand sem trocar de cliente,
    // `getLimit` continuaria filtrando estoque pela loja raiz e marcaria produtos da nova loja como
    // indisponíveis (o cenário "voltei pro cliente e tudo virou indisponível").
    if (!tables.current?.estoque || !activeStoreCode) return {};

    const estoqueMap = new Map<number, StockProps>();
    const mergedStock = [...tables.current.estoque, ...stockMock];
    for (const item of mergedStock) {
      if (Number(item.COD_LOJA) === Number(activeStoreCode)) {
        estoqueMap.set(item.COD_PRODUTO, item);
      }
    }
    return Object.fromEntries(estoqueMap);
  }, [tables.current?.estoque, activeStoreCode]);

  const totalQuantity = useMemo(() => {
    return currentCartDetails?.items?.length || 0;
  }, [currentCartDetails]);

  const totalVolume = useMemo(() => {
    const items = currentCartDetails?.items || [];
    return items.reduce((acc, item) => acc + (item.quantity || 0), 0);
  }, [currentCartDetails]);

  const getLimit = useCallback(
    (id: number) => {
      return dictEstoque[id]?.QTD_ESTOQUE || 0;
    },
    [dictEstoque]
  );

  const getStockMsg = useCallback((id: number) => dictEstoque[id]?.MSG_ESTOQUE, [dictEstoque]);

  const removeProductFromCart = useCallback(
    async ({ uid }: { uid: string }) => {
      if (!selectedClient?.COD_CLIENTE) return;
      
      updateLastOrderSendTime();
      const clientCart = cartDetails[currentCartDetailsKey];
      if (!clientCart?.items) return;
      
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[removeProductFromCart]: UID: ${uid}`,
      });
      const newCart = clientCart.items.filter((product) => product.uid !== uid);
      const total = newCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

      const updatedCartDetails = {
        ...cartDetails,
        [currentCartDetailsKey]: {
          items: newCart,
          quantity: total,
        },
      };

      setCart(newCart);
      setCartDetails(updatedCartDetails);
      setOnStorage('cart_details', updatedCartDetails);
    },
    [cartDetails, currentCartDetailsKey, selectedClient?.COD_CLIENTE, updateLastOrderSendTime]
  );

  const removeMultipleProductsFromCart = useCallback(
    (uids: string[]) => {
      if (!selectedClient?.COD_CLIENTE || !uids?.length) return;

      const clientCart = cartDetails[currentCartDetailsKey];
      if (!clientCart?.items?.length) return;

      const newCart = clientCart.items.filter((product) => !uids.includes(product.uid));
      if (newCart.length === clientCart.items.length) return;

      const total = newCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

      const updatedCartDetails = {
        ...cartDetails,
        [currentCartDetailsKey]: {
          items: newCart,
          quantity: total,
        },
      };

      setCartDetails(updatedCartDetails);
      setOnStorage('cart_details', updatedCartDetails);
    },
    [cartDetails, currentCartDetailsKey, selectedClient?.COD_CLIENTE]
  );

  const checkLimit = useCallback(
    (uid: string, sku: number, quantity: number, updatedCart: CartItems[]): number => {
      const packQtd = Number(uid?.split('-')[2]);
      const initialQtd = quantity * packQtd;

      let totalAmount = initialQtd;
      for (const item of updatedCart) {
        if (item.sku === sku && uid !== item.uid) {
          const [, qtd] = item.option_uid.split('_');
          totalAmount += item.quantity * Number(qtd);
        }
      }

      const limit = getLimit(sku);
      const stock = stockLeft?.[sku]?.stock_left ?? 0;
      const hasStockInfo = stockLeft?.[sku]?.hasOwnProperty('stock_left');
      const availableQty = hasStockInfo ? stock : limit;

      if (availableQty < totalAmount) {
        const remainingQty = availableQty - (totalAmount - initialQtd);
        return Math.max(0, Math.trunc(remainingQty / packQtd));
      }

      return quantity;
    },
    [getLimit, stockLeft]
  );

  const saveOrderNumber = useCallback((client: ClientProps, orderNumber: string) => {
    if (!client?.COD_CLIENTE) return;

    const storedOrders = { ...(getOnStorage('order_numbers') || {}) };
    const scopedKey = buildCartDetailsKey(client.COD_CLIENTE, getCartBrandKeyFromStorage());
    const legacyKey = String(client.COD_CLIENTE);
    const lastUpdate = new Date().getTime();
    if (storedOrders[legacyKey]) delete storedOrders[legacyKey];
    setOnStorage('order_numbers', {
      ...storedOrders,
      [scopedKey]: { orderNumber, lastUpdate },
    });
  }, []);

  const generateOrderNumber = useCallback(() => {
    if (!selectedClientScope?.COD_CLIENTE) return '';
    const seqpessoa = selectedClientScope.COD_RCA_CLIENTE ?? selectedClientScope.COD_RCA;
    const seqNum = Number(seqpessoa);
    if (!Number.isFinite(seqNum)) return '';

    const now = new Date();
    const cartOpenedAt = format(now, 'yyyy-MM-dd HH:mm:ss', { locale: ptBR });
    const date = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');

    const orderNumber = `${seqNum}${date}${month}${year}${hours}${minutes}${seconds}`;
    setOnStorage('cart_opened_at', cartOpenedAt);
    saveOrderNumber(selectedClientScope, orderNumber);

    return orderNumber;
  }, [selectedClientScope, saveOrderNumber]);

  const getOrGenerateOrderNumber = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return '';

    const storedOrders = { ...(getOnStorage('order_numbers') || {}) };
    const scopedKey = buildCartDetailsKey(selectedClient.COD_CLIENTE, selectedBrandKey);
    const legacyKey = String(selectedClient.COD_CLIENTE);

    let orderNumber =
      storedOrders[scopedKey]?.orderNumber ?? storedOrders[legacyKey]?.orderNumber;

    if (!orderNumber) {
      return generateOrderNumber();
    }

    if (!storedOrders[scopedKey]?.orderNumber && storedOrders[legacyKey]?.orderNumber) {
      storedOrders[scopedKey] = {
        orderNumber,
        lastUpdate: storedOrders[legacyKey]!.lastUpdate,
      };
      delete storedOrders[legacyKey];
      setOnStorage('order_numbers', storedOrders);
    }

    return orderNumber;
  }, [selectedClient, selectedBrandKey, generateOrderNumber]);

  useEffect(() => {
    if (!selectedClient?.COD_CLIENTE) {
      setHasOrderNumberSaved(false);
      return;
    }

    const storedOrders = { ...(getOnStorage('order_numbers') || {}) };
    const scopedKey = buildCartDetailsKey(selectedClient.COD_CLIENTE, selectedBrandKey);
    const legacyKey = String(selectedClient.COD_CLIENTE);
    const clientCart = cartDetails[currentCartDetailsKey];
    const clientOrder = storedOrders[scopedKey] ?? storedOrders[legacyKey];

    // Se não existir orderNumber e houver itens no carrinho, gera um novo orderNumber
    if (!clientOrder?.orderNumber && clientCart && clientCart.items && clientCart.items.length > 0) {
      const newOrderNumber = generateOrderNumber();
      if (storedOrders[legacyKey]) delete storedOrders[legacyKey];
      storedOrders[scopedKey] = { orderNumber: newOrderNumber, lastUpdate: Date.now() };
      setOnStorage('order_numbers', storedOrders);
      setHasOrderNumberSaved(true);
    } else {
      setHasOrderNumberSaved(
        !!(storedOrders[scopedKey]?.orderNumber ?? storedOrders[legacyKey]?.orderNumber)
      );
    }
  }, [selectedClient, cartDetails, currentCartDetailsKey, selectedBrandKey, generateOrderNumber]);

  const createEmptyCart = useCallback(({ client }: { client: ClientProps | undefined }) => {
    if (!client?.COD_CLIENTE) return;

    const cartId = String(client.COD_CLIENTE);
    setCartId(cartId);
    setOnStorage('cart_id', cartId);
  }, []);

  const addProductToCart = useCallback(
    async ({
      sku,
      quantity,
      option_uid,
      uid,
      category_id,
    }: {
      sku: number;
      quantity: number;
      option_uid: string;
      uid: string;
      category_id: number;
    }) => {
      if (!selectedClient?.COD_CLIENTE) {
        alert('Por favor, selecione um cliente para continuar');
        return;
      }

      registerEvent({
        tipoEvento: 'CART',
        codProduto: sku,
        obsEvento: `[addProductToCart]: SKU: ${sku} | OPTION_UID: ${option_uid} | QTD: ${quantity}`,
      });

      getOrGenerateOrderNumber();
      updateLastOrderSendTime();
      setIsAdding(true);

      setCartDetails((prevCartDetails) => {
        const clientCart = prevCartDetails[currentCartDetailsKey] || { items: [] };
        const updatedCart = [...clientCart.items];

        quantity = checkLimit(uid, sku, quantity, updatedCart);
        if (quantity <= 0) return prevCartDetails;

        const existingProductIndex = updatedCart.findIndex((product) => product.sku === sku && product.uid === uid);

        const currentTime = new Date().toISOString();

        if (existingProductIndex >= 0) {
          updatedCart[existingProductIndex].quantity = quantity;
          updatedCart[existingProductIndex].addedAt = currentTime; // Save the time of addition
        } else {
          updatedCart.push({ uid, sku, quantity, option_uid, category_id, addedAt: currentTime });
        }

        const total = updatedCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);
        const newCartDetails = {
          ...prevCartDetails,
          [currentCartDetailsKey]: { items: updatedCart, quantity: total },
        };

        setOnStorage('cart_details', newCartDetails);
        return newCartDetails;
      });

      setIsAdding(false);
    },
    [currentCartDetailsKey, selectedClient, checkLimit, getOrGenerateOrderNumber]
  );

  const updateProductAmount = useCallback(
    async ({ uid, sku, quantity }: { uid: string; sku: number; quantity: number }) => {
      if (!selectedClient?.COD_CLIENTE) {
        alert('Por favor, selecione um cliente para continuar');
        return;
      }

      getOrGenerateOrderNumber();
      updateLastOrderSendTime();
      const clientCart = cartDetails[currentCartDetailsKey];
      if (!clientCart?.items) return;

      const updatedCart = [...clientCart.items];
      quantity = checkLimit(uid, sku, quantity, updatedCart);

      if (quantity < 0) return;

      const productIndex = updatedCart.findIndex((item) => item.sku === sku && item.uid === uid);
      
      const action = updatedCart[productIndex].quantity > quantity ? 'decrease' : 'increase';
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[updateProductAmount]: SKU: ${sku} | UID: ${uid} | QTD: ${quantity} | ACTION: ${action}`,
      });

      if (productIndex >= 0) {
        updatedCart[productIndex].quantity = quantity;
        const total = updatedCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

        setCart(updatedCart);
        setCartDetails({
          ...cartDetails,
          [currentCartDetailsKey]: {
            items: updatedCart,
            quantity: total,
          },
        });
        setOnStorage('cart_details', {
          ...cartDetails,
          [currentCartDetailsKey]: {
            items: updatedCart,
            quantity: total,
          },
        });
      }
    },
    [cartDetails, currentCartDetailsKey, selectedClient, checkLimit, getOrGenerateOrderNumber, stockLeft]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    removeFromStorage('cart');
  }, []);

  /**
   * Limpa um único carrinho (loja/bandeira ativa), identificado por `clientId:brandKey`.
   * `scopeCartKey` (ex.: expiração) limpa só essa entrada; sem argumento, usa o escopo ativo.
   */
  const handleRemoveItems = useCallback(
    async (scopeCartKey?: string) => {
      const keyToClear = scopeCartKey ?? currentCartDetailsKey;
      if (!keyToClear) return;

      const cartOwnerId = Number(keyToClear.split(':')[0]);
      if (!Number.isFinite(cartOwnerId)) return;

      const clearingActiveScope =
        keyToClear === currentCartDetailsKey && cartOwnerId === selectedClient?.COD_CLIENTE;

      if (clearingActiveScope) {
        updateLastOrderSendTime();
        clearClientDiscount(cartOwnerId);
      }

      const updatedCartDetails = {
        ...cartDetails,
        [keyToClear]: { items: [], quantity: 0 },
      };

      setCartDetails(updatedCartDetails);
      setOnStorage('cart_details', updatedCartDetails);

      const orders = {
        ...(getOnStorage('order_numbers') as Record<string, { lastUpdate: number; orderNumber: string }>),
      };
      delete orders[keyToClear];
      const legacyKey = String(cartOwnerId);
      if (clearingActiveScope && orders[legacyKey]) {
        delete orders[legacyKey];
      }
      setOnStorage('order_numbers', orders);
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[handleRemoveItems]: CLEAR_CART`,
      });
    },
    [
      cartDetails,
      currentCartDetailsKey,
      selectedClient?.COD_CLIENTE,
      updateLastOrderSendTime,
      clearClientDiscount,
    ]
  );

  useEffect(() => {
    if (!selectedClient?.COD_CLIENTE) {
      setHasOrderNumberSaved(false);
      return;
    }

    const storedOrders = getOnStorage('order_numbers') || {};
    const scopedKey = buildCartDetailsKey(selectedClient.COD_CLIENTE, selectedBrandKey);
    const legacyKey = String(selectedClient.COD_CLIENTE);
    setHasOrderNumberSaved(
      !!(storedOrders[scopedKey]?.orderNumber ?? storedOrders[legacyKey]?.orderNumber)
    );
  }, [selectedClient, selectedBrandKey]);

  useEffect(() => {
    setOnStorage('cart_sort', cartSort);
  }, [cartSort]);

  useEffect(() => {
    const updateBrandKey = () => {
      setSelectedBrandKey(getCartBrandKeyFromStorage());
    };

    window.addEventListener('selected-brand-changed', updateBrandKey);
    window.addEventListener('storage', updateBrandKey);
    return () => {
      window.removeEventListener('selected-brand-changed', updateBrandKey);
      window.removeEventListener('storage', updateBrandKey);
    };
  }, []);

  useEffect(() => {
    if (!currentCartDetailsKey) {
      setCart([]);
      return;
    }

    setCart(cartDetails[currentCartDetailsKey]?.items || []);
  }, [cartDetails, currentCartDetailsKey]);

  useEffect(() => {
    const now = Date.now();
    const orders = {
      ...(getOnStorage('order_numbers') || {}),
    } as Record<string, { lastUpdate?: number; orderNumber?: string }>;
    let warnedForCurrentClient = false;

    for (const orderKey of Object.keys(orders)) {
      const entry = orders[orderKey];
      if (!entry?.lastUpdate) continue;
      if (entry.lastUpdate >= now - MAX_TIME_TO_KEEP_ORDER_NUMBER) continue;

      // Legado: chave só numérica (pedido por COD_CLIENTE) — limpa todos os carrinhos daquele cliente.
      if (/^\d+$/.test(orderKey)) {
        const clientId = Number(orderKey);
        const rawDetails = { ...(getOnStorage('cart_details') || {}) };
        for (const ck of Object.keys(rawDetails)) {
          if (ck.startsWith(`${clientId}:`)) {
            rawDetails[ck] = { items: [], quantity: 0 };
          }
        }
        setOnStorage('cart_details', rawDetails);
        delete orders[orderKey];
        if (clientId === selectedClient?.COD_CLIENTE) warnedForCurrentClient = true;
        continue;
      }

      // Formato multiloja: `COD_CLIENTE:brandKey` — remove só esse carrinho.
      const rawDetails = { ...(getOnStorage('cart_details') || {}) };
      if (rawDetails[orderKey]) {
        rawDetails[orderKey] = { items: [], quantity: 0 };
        setOnStorage('cart_details', rawDetails);
      }
      delete orders[orderKey];
      const owner = Number(orderKey.split(':')[0]);
      if (owner === selectedClient?.COD_CLIENTE) warnedForCurrentClient = true;
    }

    setOnStorage('order_numbers', orders);

    if (warnedForCurrentClient) {
      showAlert({
        title: 'Carrinhos expirados foram removidos.',
        theme: 'warning',
        time: 5000,
        close: 'icon',
        bgType: 'primary',
        borderLeft: true,
        bordered: true,
      });
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      cart,
      cartId,
      cartDetails: cartDetailsWithCurrentClientAlias,
      currentCartDetails,
      totalQuantity,
      totalVolume,
      addProductToCart,
      getLimit,
      getStockMsg,
      loadedEstoque,
      removeProductFromCart,
      removeMultipleProductsFromCart,
      updateProductAmount,
      createEmptyCart,
      clearCart,
      checkLimit,
      handleRemoveItems,
      isSearching,
      setIsSearching,
      filterTerm,
      setFilterTerm,
      isAdding,
      cartLoading,
      updateLastOrderSendTime,
      setCartLoading,
      hasOrderNumberSaved,
      setCartSort,
      cartSort,
    }),
    [
      cart,
      cartId,
      cartDetailsWithCurrentClientAlias,
      currentCartDetails,
      totalQuantity,
      totalVolume,
      getStockMsg,
      updateLastOrderSendTime,
      getLimit,
      checkLimit,
      addProductToCart,
      removeProductFromCart,
      removeMultipleProductsFromCart,
      updateProductAmount,
      createEmptyCart,
      clearCart,
      handleRemoveItems,
      isSearching,
      filterTerm,
      isAdding,
      cartLoading,
      hasOrderNumberSaved,
      cartSort,
    ]
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

const useCart = () => {
  const context = useContext(CartContext);

  if (!context) throw new Error('O context useCart só pode ser usado dentro de seu escopo. Erro ao usar hook useCart');

  return context;
};

export { CartProvider, useCart };
